import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  ChallengeAlert,
  ChallengeBuilderLaunch,
  ChallengeBuilderProject,
  ChallengeBuilderPrompt,
  ChallengeGoal,
  ChallengeMetric,
  ChallengeObjective,
  ChallengeReward,
  ChallengeSalvage,
  ChallengeTarget,
  ChallengeTask,
} from '../model/project';
import {cbMock} from './challenge-builder-mock-data';

@Injectable({
  providedIn: 'root',
})
export class ChallengeBuilderApiService {
  private useMockData = false;

  private get url() {
    return `${environment.apiUrl}/projects`;
  }

  cb = cbMock.challengeBuilder;

  constructor(private http: HttpClient) {}

  getChallengeBuilderProject(projectId: string): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of(cbMock);
    }
    return this.http.get<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}`);
  }

  refineGoal(projectId: string, initialGoal: string): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of({
        _id: cbMock._id,
        challengeBuilder: {initialGoal: this.cb.initialGoal, goals: this.cb.goals},
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/refineGoal`, {
      initialGoal,
    });
  }

  updateChallengeGoal(
    projectId: string,
    goals: ChallengeGoal[],
    goalAltered: boolean
  ): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      const objectivesWithNameOnly = this.cb.objectives.map(obj => ({...obj, _id: obj._id, name: obj.name, tasks: []}));
      return of({
        _id: cbMock._id,
        challengeBuilder: {initialGoal: this.cb.initialGoal, goals: this.cb.goals, objectives: objectivesWithNameOnly},
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/goal`, {
      goals,
      goalAltered,
    });
  }

  updateChallengeObjectives(
    projectId: string,
    objectives: ChallengeObjective[],
    objectivesAltered: ChallengeObjective[]
  ): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of({
        _id: cbMock._id,
        challengeBuilder: {
          initialGoal: this.cb.initialGoal,
          goals: this.cb.goals,
          objectives: this.cb.objectives,
          tasks: this.cb.tasks,
        },
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/objectives`, {
      objectives,
      objectivesAltered,
    });
  }

  updateChallengeTasks(
    projectId: string,
    tasks: ChallengeTask[],
    tasksAltered: ChallengeTask[]
  ): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of({
        _id: cbMock._id,
        challengeBuilder: {
          initialGoal: this.cb.initialGoal,
          goals: this.cb.goals,
          objectives: this.cb.objectives,
          tasks: this.cb.tasks,
          metrics: this.cb.metrics,
        },
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/tasks`, {
      tasks,
      tasksAltered,
    });
  }

  updateChallengeMetrics(
    projectId: string,
    metrics: ChallengeMetric[],
    metricsAltered: ChallengeMetric[]
  ): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of({
        _id: cbMock._id,
        challengeBuilder: {
          initialGoal: this.cb.initialGoal,
          goals: this.cb.goals,
          objectives: this.cb.objectives,
          tasks: this.cb.tasks,
          metrics: this.cb.metrics,
          targets: this.cb.targets,
        },
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/metrics`, {
      metrics,
      metricsAltered,
    });
  }

  updateChallengeTargets(projectId: string, targets: ChallengeTarget[]): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of({
        _id: cbMock._id,
        challengeBuilder: {
          initialGoal: this.cb.initialGoal,
          goals: this.cb.goals,
          objectives: this.cb.objectives,
          tasks: this.cb.tasks,
          metrics: this.cb.metrics,
          targets: this.cb.targets,
        },
      } as ChallengeBuilderProject);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/targets`, {
      targets,
    });
  }

  updateChallengeSchedule(projectId: string, tasks: ChallengeTask[]): Observable<ChallengeBuilderProject> {
    if (this.useMockData) {
      return of(cbMock);
    }
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/checkIns`, {
      tasks,
    });
  }

  startChallenge(projectId: string, launchData: ChallengeBuilderLaunch): Observable<ChallengeBuilderProject> {
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/startChallenge`, {
      ...launchData,
    });
  }

  stopChallenge(projectId: string): Observable<ChallengeBuilderProject> {
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/stopChallenge`, {});
  }

  getAllChallengeBuilderPrompts(): Observable<ChallengeBuilderPrompt[]> {
    return this.http.get<ChallengeBuilderPrompt[]>(`${this.url}/challengeBuilder/prompts`);
  }

  updateChallengeBuilderPrompts(prompts: ChallengeBuilderPrompt[]): Observable<ChallengeBuilderPrompt[]> {
    return this.http.put<ChallengeBuilderPrompt[]>(`${this.url}/challengeBuilder/prompts`, {prompts});
  }

  addSalvage(projectId: string, salvage: ChallengeSalvage): Observable<ChallengeBuilderProject> {
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/salvage`, {...salvage});
  }

  updateSalvage(projectId: string, salvage: ChallengeSalvage): Observable<ChallengeBuilderProject> {
    return this.http.patch<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/salvage`, {
      ...salvage,
    });
  }

  deleteSalvage(projectId: string, salvageId: string): Observable<ChallengeBuilderProject> {
    return this.http.delete<ChallengeBuilderProject>(
      `${this.url}/challengeBuilder/${projectId}/salvage?salvageId=${salvageId}`
    );
  }

  addReward(projectId: string, reward: ChallengeReward): Observable<ChallengeBuilderProject> {
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/reward`, {...reward});
  }

  updateReward(projectId: string, reward: ChallengeReward): Observable<ChallengeBuilderProject> {
    return this.http.patch<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/reward`, {
      ...reward,
    });
  }

  deleteReward(projectId: string, rewardId: string): Observable<ChallengeBuilderProject> {
    return this.http.delete<ChallengeBuilderProject>(
      `${this.url}/challengeBuilder/${projectId}/reward?rewardId=${rewardId}`
    );
  }

  addAlert(projectId: string, alert: ChallengeAlert): Observable<ChallengeBuilderProject> {
    return this.http.post<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/alert`, {...alert});
  }

  updateAlert(projectId: string, alert: ChallengeAlert): Observable<ChallengeBuilderProject> {
    return this.http.patch<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/alert`, {
      ...alert,
    });
  }

  deleteAlert(projectId: string, alertId: string): Observable<ChallengeBuilderProject> {
    return this.http.delete<ChallengeBuilderProject>(
      `${this.url}/challengeBuilder/${projectId}/alert?alertId=${alertId}`
    );
  }

  updateTaskIcon(projectId: string, taskId: string, icon: string): Observable<void> {
    return this.http.patch<void>(`${this.url}/challengeBuilder/${projectId}/task/${taskId}/icon`, {
      icon,
    });
  }

  toggleAi(projectId: string): Observable<ChallengeBuilderProject> {
    return this.http.patch<ChallengeBuilderProject>(`${this.url}/challengeBuilder/${projectId}/toggleAi`, {});
  }
}
